import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Chip, Grid, Button, Card, CardMedia, CardContent, IconButton, Dialog, DialogTitle, DialogContent, Collapse } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LockIcon from "@mui/icons-material/Lock";
import CloseIcon from "@mui/icons-material/Close";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { useMyContext } from "../../store/ContextApi";
import { getPublicCourseCompleteDetail } from "../../State/Course/Action";
import { addCourseToCart } from "../../State/Cart/Action";

const toTitleCase = (str) => {
  if (!str) return str;
  return str
    .split(/[ _-]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

const CourseDetail = () => {
  const { courseId } = useParams();
  const [videoDialogOpen, setVideoDialogOpen] = React.useState(false);
  const [videoUrl, setVideoUrl] = React.useState("");
  const [selectedChapterTitle, setSelectedChapterTitle] = React.useState("");
  const [openModuleId, setOpenModuleId] = React.useState(null);
  const [openDescriptionByModuleId, setOpenDescriptionByModuleId] = React.useState({});
  const [openDescriptionByChapterId, setOpenDescriptionByChapterId] = React.useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token } = useMyContext();
  const course = useSelector((store) => store.course.courseNestedDetail || store.course.course);
  const isLoading = useSelector((store) => store.course.isLoading);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (courseId) {
      dispatch(getPublicCourseCompleteDetail(courseId));
    }
  }, [dispatch, courseId]);

  if (isLoading && !course) {
    return (
      <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body1">Loading course...</Typography>
      </Box>
    );
  }

  if (!course) {
    return (
      <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body1">Course not found.</Typography>
      </Box>
    );
  }

  const finalPrice = Math.max(0, (course.price || 0) - (course.discountAmount || 0));
  const hasVideo = Array.isArray(course.modules)
    ? course.modules.some((m) => Array.isArray(m.chapters) && m.chapters.some((c) => c.type === "VIDEO"))
    : false;

  const getPlayableUrl = (rawUrl) => {
    if (!rawUrl) return rawUrl;
    try {
      const url = new URL(rawUrl);
      // YouTube standard/watch links -> embed with autoplay
      if (url.hostname.includes("youtube.com")) {
        const videoId = url.searchParams.get("v");
        if (videoId) return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
        // already /embed/
        if (url.pathname.includes("/embed/")) {
          url.searchParams.set("autoplay", "1");
          return url.toString();
        }
      }
      // youtu.be short links
      if (url.hostname === "youtu.be") {
        const id = url.pathname.replace("/", "");
        if (id) return `https://www.youtube.com/embed/${id}?autoplay=1`;
      }
      // Vimeo
      if (url.hostname.includes("vimeo.com")) {
        url.searchParams.set("autoplay", "1");
        return url.toString();
      }
      return rawUrl;
    } catch {
      return rawUrl;
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(135deg,#eef2ff,#fdf2f8)", py: 6, px: 2 }}>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 3, overflow: "hidden", boxShadow: 4 }}>
              <CardMedia component="img" image={course.thumbnailUrl} alt={course.name} sx={{ height: 280, objectFit: "cover" }} />
              <CardContent>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {course.name}
                </Typography>
                {course.type && (
                  <Chip label={toTitleCase(course.type)} color="primary" variant="outlined" size="small" />
                )}
                <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  {course.discountAmount ? (
                    <>
                      <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.secondary" }}>₹{course.price}</Typography>
                      <Typography variant="h6" color="primary" fontWeight={800}>₹{finalPrice}</Typography>
                      <Typography variant="caption" sx={{ color: "success.main", fontWeight: 600 }}>(₹{course.discountAmount} Off)</Typography>
                    </>
                  ) : (
                    <Typography variant="h6" color="primary" fontWeight={800}>₹{course.price}</Typography>
                  )}
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      if (!token && !localStorage.getItem('JWT_TOKEN')) {
                        navigate('/login');
                      } else {
                        navigate(`/course-complete-detail/${course.id}`);
                      }
                    }}
                  >
                    Start Learning
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Box sx={{ p: { xs: 0, md: 2 } }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                About this course
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
                {course.description}
              </Typography>

              <Typography variant="h6" fontWeight={700} gutterBottom>
                Modules & Chapters
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {course.modules?.map((m, idx) => {
                  const isOpen = openModuleId === m.id;
                  const isDescOpen = !!openDescriptionByModuleId[m.id];
                  return (
                    <Box key={m.id} sx={{ p: 2, borderRadius: 2, backgroundColor: 'white', boxShadow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                        <Box
                          component="span"
                          sx={{
                            width: '36px !important',
                            height: '36px !important',
                            maxWidth: '36px !important',
                            maxHeight: '36px !important',
                            minWidth: '36px !important',
                            minHeight: '36px !important',
                            flexBasis: '36px',
                            flexGrow: 0,
                            flexShrink: 0,
                            aspectRatio: '1 / 1',
                            borderRadius: '9999px',
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.9rem',
                            fontWeight: 700,
                            p: 0,
                            lineHeight: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            boxShadow: 1,
                            boxSizing: 'border-box'
                          }}
                        >
                          {m.orderIndex ?? idx + 1}
                        </Box>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>{m.title}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>
                          {m.chapters?.length || 0} chapters • {m.length || course.length || ''}
                        </Typography>
                        {m.description && (
                          <IconButton
                            size="small"
                            onClick={() => setOpenDescriptionByModuleId((prev) => ({ ...prev, [m.id]: !isDescOpen }))}
                            aria-label={isDescOpen ? 'Hide description' : 'Show description'}
                          >
                            {isDescOpen ? (
                              <VisibilityOffOutlinedIcon fontSize="small" />
                            ) : (
                              <VisibilityOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => setOpenModuleId(isOpen ? null : m.id)} aria-label={isOpen ? 'Collapse' : 'Expand'}>
                          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                      {m.description && (
                        <Collapse in={isDescOpen} timeout="auto" unmountOnExit>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            {m.description}
                          </Typography>
                        </Collapse>
                      )}
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                          {m.chapters?.map((c) => (
                            <li key={c.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>{c.title}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.disabled' }}>{c.length || '0h 0m'}</Typography>
                                {c.description && (
                                  <IconButton
                                    size="small"
                                    onClick={() => setOpenDescriptionByChapterId((prev) => ({ ...prev, [c.id]: !prev[c.id] }))}
                                    aria-label={openDescriptionByChapterId[c.id] ? 'Hide description' : 'Show description'}
                                    sx={{ p: 0.5 }}
                                  >
                                    {openDescriptionByChapterId[c.id] ? (
                                      <VisibilityOffOutlinedIcon fontSize="small" />
                                    ) : (
                                      <VisibilityOutlinedIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                )}
                                {c.type === 'VIDEO' && c.previewAvailable ? (
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const url = c.videoUrl || c.videoURL || '';
                                      if (url) {
                                        setVideoUrl(getPlayableUrl(url));
                                      } else {
                                        setVideoUrl('');
                                      }
                                      setSelectedChapterTitle(c.title || 'Video Preview');
                                      setVideoDialogOpen(true);
                                    }}
                                    aria-label="Play"
                                    color="primary"
                                    sx={{ p: 0.5, ml: 'auto' }}
                                  >
                                    <PlayArrowIcon />
                                  </IconButton>
                                ) : (
                                  <IconButton
                                    size="small"
                                    onClick={async () => {
                                      if (!token && !localStorage.getItem('JWT_TOKEN')) {
                                        navigate('/login');
                                      } else {
                                        await dispatch(addCourseToCart(course.id));
                                        navigate('/show-my-cart');
                                      }
                                    }}
                                    aria-label="Add to cart"
                                    sx={{ p: 0.5, ml: 'auto' }}
                                    color="default"
                                  >
                                    <LockIcon />
                                  </IconButton>
                                )}
                              </Box>
                              {c.description && (
                                <Collapse in={!!openDescriptionByChapterId[c.id]} timeout="auto" unmountOnExit>
                                  <Box
                                    sx={{
                                      mt: 0.75,
                                      p: 1.25,
                                      borderRadius: 1,
                                      bgcolor: 'rgba(102, 126, 234, 0.06)',
                                      border: '1px solid',
                                      borderColor: 'rgba(102, 126, 234, 0.2)'
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                      <Box
                                        sx={{
                                          width: 24,
                                          height: 24,
                                          borderRadius: '50%',
                                          bgcolor: 'rgba(102, 126, 234, 0.12)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: '#667eea',
                                          flex: '0 0 auto'
                                        }}
                                      >
                                        <ArticleOutlinedIcon sx={{ fontSize: '0.95rem' }} />
                                      </Box>
                                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {c.description}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Collapse>
                              )}
                            </li>
                          ))}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Video Dialog */}
      <Dialog
        open={videoDialogOpen}
        onClose={() => {
          setVideoDialogOpen(false);
          setSelectedChapterTitle("");
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          }
        }}
      >
        <Box sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VideoLibraryIcon sx={{ fontSize: '1.5rem' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {selectedChapterTitle || 'Video Preview'}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setVideoDialogOpen(false);
              setSelectedChapterTitle("");
            }}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, bgcolor: '#000' }}>
          {videoUrl ? (
            <Box sx={{ 
              position: 'relative', 
              width: '100%', 
              height: { xs: 250, sm: 400, md: 540 },
              background: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1
              }}>
                <Box sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.3,
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { transform: 'scale(1)', opacity: 0.3 },
                    '50%': { transform: 'scale(1.1)', opacity: 0.5 },
                  },
                }}>
                  <PlayArrowIcon sx={{ fontSize: '2rem', color: 'white' }} />
                </Box>
              </Box>
              <iframe
                width="100%"
                height="100%"
                src={videoUrl}
                title="Chapter Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ 
                  display: 'block',
                  position: 'relative',
                  zIndex: 2
                }}
              />
            </Box>
          ) : (
            <Box sx={{ 
              p: 6, 
              textAlign: 'center',
              bgcolor: 'rgba(102, 126, 234, 0.05)',
              minHeight: { xs: 250, sm: 400, md: 540 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Box sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                opacity: 0.7
              }}>
                <VideoLibraryIcon sx={{ fontSize: '3rem', color: 'white' }} />
              </Box>
              <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 600, mb: 1 }}>
                No Preview Available
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                This video preview is not available at the moment.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CourseDetail;


